import { JSX } from "react";
import { toSafeNumber } from "src/charts/data/transformers";

export type KpiComparisonData = {
  prior_value?: number | string | null;
  change?: number | string | null;
  change_percent?: number | string | null;
  trend?: string | null;
};

const resolveTrend = (
  comparison: KpiComparisonData,
): "up" | "down" | "neutral" => {
  const normalizedTrend = String(comparison.trend ?? "")
    .trim()
    .toLowerCase();

  if (
    normalizedTrend === "up" ||
    normalizedTrend === "down" ||
    normalizedTrend === "neutral"
  ) {
    return normalizedTrend;
  }

  const numericChange =
    comparison.change_percent !== undefined &&
    comparison.change_percent !== null
      ? toSafeNumber(comparison.change_percent)
      : toSafeNumber(comparison.change);

  if (numericChange > 0) {
    return "up";
  }

  if (numericChange < 0) {
    return "down";
  }

  return "neutral";
};

const resolveChangePercent = (comparison: KpiComparisonData): number => {
  if (
    comparison.change_percent !== undefined &&
    comparison.change_percent !== null
  ) {
    return toSafeNumber(comparison.change_percent);
  }

  const priorValue = toSafeNumber(comparison.prior_value);
  const changeValue = toSafeNumber(comparison.change);

  if (priorValue === 0) {
    return changeValue;
  }

  return (changeValue / priorValue) * 100;
};

const formatTrendPercent = (value: number): string => {
  const rounded = Math.round(value * 10) / 10;
  const normalized = Number.isInteger(rounded)
    ? `${rounded}`
    : rounded.toFixed(1).replace(/\.0$/, "");

  return `${normalized}%`;
};

export default function KpiComparison({
  comparison,
}: {
  comparison?: KpiComparisonData | null;
}): JSX.Element | null {
  if (!comparison) {
    return null;
  }

  const trend = resolveTrend(comparison);
  const changePercent = Math.abs(resolveChangePercent(comparison));
  const sign = trend === "up" ? "+" : trend === "down" ? "-" : "";
  const trendIcon =
    trend === "up"
      ? "bi-arrow-up-right"
      : trend === "down"
        ? "bi-arrow-down-right"
        : "bi-dash-lg";
  return (
    <div className="kpi-counter-comparison">
      <span
        className={`kpi-counter-comparison__chip kpi-counter-comparison__chip--${trend}`}
      >
        <i className={`bi ${trendIcon}`} aria-hidden="true" />
        <span>
          {sign}
          {formatTrendPercent(changePercent)} vs Prior
        </span>
      </span>
    </div>
  );
}
